import { useCallback, useEffect, useState, type ReactNode } from "react";

import { services, type Transport } from "@tdelivr/api-client";
import type { WorkforceMember } from "@tdelivr/api-client/generated/identity/workforce-identity.js";

import { boundedNote } from "../board.ts";
import type { Session } from "../session.ts";
import {
  CLOSING_NOTE,
  INVITE_REFUSAL,
  LISTING_NOTE,
  STAFF_SCOPE,
  staffRow,
  stateOf,
  statesPresent,
} from "../staff.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { RecordsBoard } from "./RecordsBoard.tsx";

/**
 * `staff` in `22 WP21 Merchant Portal.dc.html`: who at this shop can accept orders or change the
 * menu.
 *
 * **The one of this portal's three blocked pages that was blocked on a resource rather than a
 * route**, which its own recorded reason said in as many words: _"This is a resource that does not
 * exist, not a route that is not published."_ So it could not have been served in the cohort that
 * published three listings for this portal — publishing a fourth would not have helped.
 * [ADR 0084](../../../docs/adr/0084-a-staff-member-is-workforce-identity-and-nothing-held-one.md)
 * adds `workforce_members` to IAM-02, and this board is one of the two screens it unblocks.
 *
 * What is drawn from data and what is not — `../staff.ts` argues each:
 *
 * | drawn                            | source                                                        |
 * | -------------------------------- | ------------------------------------------------------------- |
 * | the rows                         | IAM-02's member listing, filtered to this merchant            |
 * | `Person` — `Grace Njeri`         | `displayName`. The second real name on this portal            |
 * | `Role` — `Manager`               | `role`, the tenant's own words                                 |
 * | `Branch` — `Kilimani`            | `locationId`. **The name is MER-02's**                        |
 * | `done · active`, `waiting · invited` | `status`. **Both designed states are published**           |
 * | the four tiles                   | no listing publishes a total (R-04); no tile row here         |
 * | the primary command              | drawn and refused — `INVITE_REFUSAL`                          |
 * | the closing note                 | **replaced** — `CLOSING_NOTE` says why                        |
 *
 * **This is the only board on this portal whose rows somebody else adds**, and that is worth saying
 * on the page rather than leaving a merchant to infer it from a disabled button. A staff record
 * belongs to the tenant; a shop sees the people recorded against it. `LISTING_NOTE` is that
 * sentence.
 *
 * **It reads the merchant and not the branch**, which separates it from the stock board beside it.
 * The design draws three rows across two branches, so this board is the shop's whole staff — and
 * sending `locationId` as well would have hidden the shop-wide manager the design's own first row
 * might be.
 */

const workforce = services.identity__workforce_identity;

export interface StaffProps {
  readonly transport: Transport;
  readonly session: Session;
  /** Innermost last: `EAM › Business › Staff`, the design's trail. */
  readonly crumbs: readonly string[];
}

/** The design's `PAGES` row, so the words on screen and the words here cannot drift apart. */
const TITLE = "Staff";
const SOURCE = "IAM-02";
const DESCRIPTION = "Who at your shop can accept orders or change the menu.";
const COLUMNS = ["Person", "Role", "Branch"] as const;
/** The design's own `noteK` for this page is `info`, which its `NK` map draws in the sky tint. */
const NOTE_KIND = "info" as const;
/** The design's own `FORMS.staff[0]`, which is the word on its heading button. */
const PRIMARY = "Invite staff";

/** The most rows one request answers, which is this listing's own maximum. */
const PER_FETCH = 200;

/** IAM-02's published statuses, so a value outside them is read as the safe one. */
const STATUSES: readonly WorkforceMember["status"][] = ["invited", "active", "suspended"];

/**
 * Narrow one published row onto the shape this board reads.
 *
 * A status the contract's enum does not cover is read as `suspended`, which is the safe direction
 * for the same reason the products board reads an unknown status as suspended: `suspended` means
 * this person cannot sign in, and a member whose state this portal cannot read must not be drawn as
 * active — a manager would believe somebody still had access.
 */
function memberOf(row: WorkforceMember): WorkforceMember {
  return {
    ...row,
    status: STATUSES.find((candidate) => candidate === row.status) ?? "suspended",
  };
}

export function Staff({ transport, session, crumbs }: StaffProps): ReactNode {
  const [rows, setRows] = useState<readonly WorkforceMember[] | undefined>(undefined);
  const [truncated, setTruncated] = useState(false);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [state, setState] = useState("");

  const load = useCallback(async () => {
    setRows(undefined);
    setFailure(undefined);

    if (!session.scopes.has(STAFF_SCOPE)) {
      setFailure({ kind: "forbidden", missingScopes: [STAFF_SCOPE] });
      return;
    }

    /*
     * The merchant, from the token.
     *
     * Not the branch, unlike the stock board: the design draws two branches on one board, so this
     * is the shop's staff. And not from a path or a selector, for the reason every merchant-held
     * value on this portal comes off the token — a merchant the browser supplies is a merchant the
     * browser can change, and another shop's staff list is a list of names.
     */
    const merchantId = session.merchantId;
    if (merchantId === undefined) {
      setFailure({
        kind: "forbidden",
        missingScopes: ["a merchant_id claim on your token"],
      });
      return;
    }

    try {
      const page = await workforce
        .createWorkforceIdentityClient(transport)
        .listWorkforceMembers(session.tenantId, String(PER_FETCH), undefined, merchantId);
      setRows(page.members.map(memberOf));
      setTruncated(typeof page.nextAfter === "string" && page.nextAfter.length > 0);
    } catch (error) {
      setFailure(stateForError(error, [STAFF_SCOPE]));
    }
  }, [transport, session.scopes, session.tenantId, session.merchantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const boardRows =
    rows === undefined
      ? undefined
      : rows.filter((row) => state === "" || stateOf(row).label === state).map(staffRow);

  const bounded = boundedNote(rows?.length, truncated, "people", "newest first");

  return (
    <RecordsBoard
      crumbs={crumbs}
      title={TITLE}
      source={SOURCE}
      description={DESCRIPTION}
      columns={COLUMNS}
      rows={boardRows}
      {...(failure === undefined ? {} : { failure })}
      onRetry={() => {
        void load();
      }}
      purpose="Nobody is recorded against this shop yet. A person appears here once the tenant admin console invites one."
      primary={{ label: PRIMARY, refusal: INVITE_REFUSAL }}
      filter={{
        // The design's own word for unfiltered, from its `gridUI`: `All states`.
        unfiltered: "All states",
        options: (rows === undefined ? [] : statesPresent(rows)).map((label) => ({
          value: label,
          label,
        })),
        value: state,
        onChange: setState,
      }}
      note={{ kind: NOTE_KIND, glyph: "info", text: CLOSING_NOTE }}
      {...(bounded === undefined ? {} : { bounded })}
      footnote={LISTING_NOTE}
      exportName="staff"
      detailSubtitle={`${TITLE} · ${SOURCE}`}
      detailNote={CLOSING_NOTE}
    />
  );
}
